import { useMemo } from 'react'
import { useFeatures } from '../features'
import { formatRate, type MessageKey, useI18n } from '../i18n'
import type { Member } from '../protocol'
import { displayNick, type RoomClient, type RoomState, sourceTabKey } from '../room/client'
import { Avatar } from '../ui/Avatar'
import { Icon } from '../ui/Icon'

export function EmptyScreen({ state, canControl }: { state: RoomState; canControl: boolean }) {
  const { t } = useI18n()
  const catalog = !!useFeatures()?.catalog
  const m = state.media
  let title: MessageKey
  let hint: string | null = null
  if (m?.status === 'analyzing') {
    title = 'empty.analyzing'
    hint = t('empty.analyzingHint')
  } else if (m?.status === 'error') {
    title = 'empty.failed'
    hint = m.error ?? null
  } else if (state.source?.status === 'metadata') {
    title = 'empty.metadata'
    hint = t('empty.metadataHint')
  } else if (state.source?.status === 'error') {
    title = 'empty.sourceFailed'
    hint = state.source.error ?? null
  } else if (state.source?.status === 'ready') {
    title = canControl ? 'empty.choose' : 'empty.ownerChoosing'
  } else if (catalog && !state.title) {
    // First what to watch, then a release for it.
    title = canControl ? 'empty.chooseTitle' : 'empty.noTitle'
    hint = canControl ? t('empty.chooseTitleHint', { tab: t(sourceTabKey(state)) }) : null
  } else if (catalog) {
    title = canControl ? 'empty.chooseRelease' : 'empty.noTorrent'
    hint = canControl ? t('empty.chooseReleaseHint', { tab: t(sourceTabKey(state)) }) : null
  } else {
    title = canControl ? 'empty.addTorrent' : 'empty.noTorrent'
    hint = canControl ? t('empty.addHint', { tab: t(sourceTabKey(state)) }) : null
  }
  return (
    <div className="player__empty">
      <p className="player__empty-title">{t(title)}</p>
      {hint && <p className="player__empty-hint">{hint}</p>}
    </div>
  )
}

/**
 * Whether a user may continue without those waited for: the owner, or
 * controllers while the owner is away (as the server decides).
 */
export function canDecideWait(state: RoomState): boolean {
  const role = state.you?.role
  if (role === 'owner') return true
  return role === 'controller' && !state.members.some((m) => m.role === 'owner' && m.online)
}

/**
 * What the room is waiting for: data from the torrent (nobody's fault) and
 * members whose own connection is slow. The owner can go on without them:
 * all at once, one member this time, or one member for good.
 */
export function WaitingCard({ client, state }: { client: RoomClient; state: RoomState }) {
  const { t } = useI18n()
  const pb = state.playback
  const people = (pb.waitingFor ?? []).map((id) => state.members.find((m) => m.userId === id)).filter((m) => !!m)
  const stats = state.source?.stats
  const decide = canDecideWait(state)
  const isOwner = state.you?.role === 'owner'
  if (!pb.loadingSource && people.length === 0) {
    return (
      <div className="waiting" role="status">
        <p className="waiting__title">{t('wait.starting')}</p>
      </div>
    )
  }
  return (
    <div className="waiting" role="status">
      {pb.loadingSource && pb.encoding && (
        <div className="waiting__source">
          <p className="waiting__title waiting__title--source">{t('wait.encoding')}</p>
          <p className="waiting__stats">{t('wait.encodingHint')}</p>
          <span className="waiting__indeterminate" aria-hidden="true" />
          {decide && people.length === 0 && (
            <button type="button" className="btn btn--primary btn--wide waiting__continue" onClick={() => client.send({ t: 'force_start' })}>
              {t('wait.continue')}
            </button>
          )}
        </div>
      )}
      {pb.loadingSource && !pb.encoding && (
        <div className="waiting__source">
          <p className="waiting__title waiting__title--source">{t('wait.loading')}</p>
          {stats && (
            <p className="waiting__stats">
              {stats.peers === 0 ? t('wait.noPeers') : t('wait.torrent', { rate: formatRate(stats.downloadRate), n: stats.peers })}
            </p>
          )}
          <span className="waiting__indeterminate" aria-hidden="true" />
        </div>
      )}
      {people.length > 0 && (
        <>
          <p className="waiting__title">{t('wait.members')}</p>
          <ul className="waiting__list">
            {people.map((m) => (
              <WaitedMember key={m.userId} member={m} client={decide ? client : null} canNever={isOwner} />
            ))}
          </ul>
          {decide && (
            <button type="button" className="btn btn--primary btn--wide waiting__continue" onClick={() => client.send({ t: 'force_start' })}>
              {t('wait.continue')}
            </button>
          )}
        </>
      )}
    </div>
  )
}

/** One member waited for: how far they got, and the owner's choices. */
function WaitedMember({ member, client, canNever }: { member: Member; client: RoomClient | null; canNever: boolean }) {
  const { t } = useI18n()
  const nick = displayNick(member.nick)
  const progress = Math.min(1, member.buffered / 4)
  return (
    <li className="waiting__member">
      <Avatar src={member.avatar} name={nick} className="waiting__avatar" />
      <div className="waiting__who">
        <span className="waiting__name">
          <span className="waiting__nick" title={nick}>
            {nick}
          </span>
          <span className="waiting__pct">{Math.round(progress * 100)}%</span>
        </span>
        <span className="waiting__bar" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${progress * 100}%` }} />
        </span>
      </div>
      {client && (
        <div className="waiting__actions">
          <button
            type="button"
            className="icon-btn"
            title={t('wait.skipOnce')}
            aria-label={t('wait.skipOnceFor', { name: nick })}
            onClick={() => client.send({ t: 'skip_member', userId: member.userId })}
          >
            <Icon name="forward" />
          </button>
          {canNever && (
            <button
              type="button"
              className="icon-btn"
              title={t('wait.never')}
              aria-label={t('wait.neverFor', { name: nick })}
              onClick={() => client.send({ t: 'set_no_wait', userId: member.userId, on: true })}
            >
              <Icon name="no-wait" />
            </button>
          )}
        </div>
      )}
    </li>
  )
}

export function Reactions({ reactions }: { reactions: RoomState['reactions'] }) {
  const items = useMemo(
    () => reactions.map((r) => ({ ...r, x: 8 + ((r.key * 37) % 30), d: (r.key % 5) * 0.08 })),
    [reactions],
  )
  return (
    <div className="reactions" aria-hidden="true">
      {items.map((r) => (
        <span key={r.key} className="reaction" style={{ right: `${r.x}%`, animationDelay: `${r.d}s` }}>
          <span className="reaction__emoji">{r.emoji}</span>
          <span className="reaction__nick">{displayNick(r.nick)}</span>
        </span>
      ))}
    </div>
  )
}
